/**
 * Column shown in the program settings import table. `field` is the property of the validated row's
 * `rawRow` (what the user typed in the file) returned by /{importerPath}/validate.
 */
export interface ProgramImportColumn {
  field: string;
  label: string;
  // Read the resolved value from `values[valuesField]` when the row has one, falling back to
  // `rawRow[field]` for rows with errors (e.g. times, which rawRow serializes server-dependently).
  valuesField?: string;
  format?: 'time';
}

export interface ProgramImportType {
  id: string;
  label: string;
  entity: string;
  importerPath: string;
  statusPath: string;
  permissions: string[];
  columns: ProgramImportColumn[];
  // Set when validate returns rows grouped per account + program ({accountId, programType,
  // [groupRowsKey]: rows}) and import expects the same groups back.
  groupRowsKey?: string;
  // Set when each group entry is a unit ({values, [unitRowsKey]: rows}) saved as a whole, with the
  // values shared by its rows instead of one per row (Service Settings).
  unitRowsKey?: string;
  // Set when the server replaces a program's whole list from its group, so removing one row would
  // silently drop it from the program. Removing a row then removes its whole account + program
  // group, and the program keeps its current values.
  removeGroupAsWhole?: boolean;
  // Section-specific warning, shown in a tooltip next to the section select (translation key).
  hint?: string;
  // Asked in a confirm dialog before import, for sections where the import also clears values
  // (translation key).
  importConfirm?: string;
  // Adds a "Week Preview" tab that shows, per account + program, the week the import will leave
  // behind. Only meaningful for sections that replace a whole schedule (Hours of Operation).
  weekPreview?: boolean;
}

// PRD-1726 separates import execution from schema management. The importer endpoints require
// program.update, while /importers-schema requires import.schemas (IamImportPermission.SCHEMAS).
export const PROGRAM_IMPORT_PERMISSIONS = ['program.update'];
export const PROGRAM_SCHEMA_PERMISSIONS = ['import.schemas'];

const serviceTimeColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'pickupEntryServiceTime', label: 'Pickup Entry Service Time'},
  {field: 'pickupExitServiceTime', label: 'Pickup Exit Service Time'},
  {field: 'pickupWorkServiceTime', label: 'Pickup Work Service Time'},
  {field: 'pickupMaximumPackageServiceTime', label: 'Pickup Maximum Package Service Time'},
  {field: 'dropoffEntryServiceTime', label: 'Dropoff Entry Service Time'},
  {field: 'dropoffExitServiceTime', label: 'Dropoff Exit Service Time'},
  {field: 'dropoffWorkServiceTime', label: 'Dropoff Work Service Time'},
  {field: 'dropoffMaximumPackageServiceTime', label: 'Dropoff Maximum Package Service Time'},
  {field: 'fieldServiceEntryServiceTime', label: 'Field Service Entry Service Time'},
  {field: 'fieldServiceExitServiceTime', label: 'Field Service Exit Service Time'},
  {field: 'fieldServiceWorkServiceTime', label: 'Field Service Work Service Time'},
  {field: 'fieldServiceMaximumPackageServiceTime', label: 'Field Service Maximum Package Service Time'},
  {field: 'allowedBlockedWaiting', label: 'Allowed Blocked Waiting'},
  {field: 'allowedBlockingWaiting', label: 'Allowed Blocking Waiting'}
];

const skillsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'skillName', label: 'Skill'},
  {field: 'enabled', label: 'Enabled'},
  {field: 'defaultSkill', label: 'Default'}
];

const hoursOfOperationColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'dayOfWeek', label: 'Day Of Week'},
  {field: 'startTime', label: 'Start Time', valuesField: 'from', format: 'time'},
  {field: 'endTime', label: 'End Time', valuesField: 'to', format: 'time'},
  {field: 'serviceStatus', label: 'Service Status'}
];

// The vehicle may be typed as id or name; resolved rows show the vehicle's name.
const vehicleSettingsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'vehicleType', label: 'Vehicle Type', valuesField: 'vehicleName'},
  {field: 'defaultVehicle', label: 'Default'},
  {field: 'enabled', label: 'Enabled'},
  {field: 'vehicleSurcharge', label: 'Vehicle Surcharge'}
];

// An empty To means the line has no upper limit.
const quantitySettingsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'quantityFrom', label: 'From'},
  {field: 'quantityTo', label: 'To'},
  {field: 'type', label: 'Type'},
  {field: 'value', label: 'Value'}
];

// One row sets all price settings of one account + program; an empty cell clears the value.
const priceSettingsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'discount', label: 'Discount'},
  {field: 'adminDiscount', label: 'Admin Discount'},
  {field: 'commission', label: 'Commission'},
  {field: 'minCommissionValue', label: 'Minimum Commission Value'},
  {field: 'pickupWaitingCycle', label: 'Pickup Waiting Cycle'},
  {field: 'pickupChargePerCycle', label: 'Pickup Charge Per Cycle'},
  {field: 'pickupGap', label: 'Pickup Gap'},
  {field: 'pickupMaximumCharge', label: 'Pickup Maximum Charge'},
  {field: 'dropoffWaitingCycle', label: 'Dropoff Waiting Cycle'},
  {field: 'dropoffChargePerCycle', label: 'Dropoff Charge Per Cycle'},
  {field: 'dropoffGap', label: 'Dropoff Gap'},
  {field: 'dropoffMaximumCharge', label: 'Dropoff Maximum Charge'},
  {field: 'lateRefundPerMinute', label: 'Late Refund Per Minute'},
  {field: 'lateMaxNoRefund', label: 'Late Max No Refund'},
  {field: 'lateMaxRefundFromDeliveryPrice', label: 'Late Max Refund From Delivery Price'},
  {field: 'taxAgency', label: 'Tax Agency'},
  {field: 'taxName', label: 'Tax Name'},
  {field: 'taxPercentage', label: 'Tax Percentage'},
  {field: 'accessorialAppointmentEnabled', label: 'Accessorial Appointment Enabled'},
  {field: 'accessorialAppointmentValue', label: 'Accessorial Appointment Value'},
  {field: 'accessorialAppointmentType', label: 'Accessorial Appointment Calculation Type'},
  {field: 'accessorialFuelEnabled', label: 'Accessorial Fuel Enabled'},
  {field: 'accessorialFuelValue', label: 'Accessorial Fuel Value'},
  {field: 'accessorialFuelType', label: 'Accessorial Fuel Calculation Type'},
  {field: 'accessorialObligateEnabled', label: 'Accessorial Obligate Enabled'},
  {field: 'accessorialObligateValue', label: 'Accessorial Obligate Value'},
  {field: 'accessorialObligateType', label: 'Accessorial Obligate Calculation Type'},
  {field: 'spotHeight', label: 'Spot Height'},
  {field: 'spotWidth', label: 'Spot Width'},
  {field: 'spotLength', label: 'Spot Length'},
  {field: 'spotWeight', label: 'Spot Weight'}
];

// One row sets one POD field (e.g. Signature) of one POD type (e.g. Pickup) of a program.
const podSettingsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'podType', label: 'POD Type'},
  {field: 'podField', label: 'POD Field'},
  {field: 'parentRelation', label: 'Parent Relation'},
  {field: 'mandatory', label: 'Mandatory'}
];

// One row sets one package size of a program. Service times are typed in minutes.
const packageSettingsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'packageName', label: 'Package'},
  {field: 'charge', label: 'Charge'},
  {field: 'enabled', label: 'Enabled'},
  {field: 'defaultSize', label: 'Default'},
  {field: 'labels', label: 'Labels'},
  {field: 'weight', label: 'Weight'},
  {field: 'height', label: 'Height'},
  {field: 'width', label: 'Width'},
  {field: 'length', label: 'Length'},
  {field: 'pickupServiceTimeMinutes', label: 'Pickup Service Time (min)'},
  {field: 'dropoffServiceTimeMinutes', label: 'Dropoff Service Time (min)'},
  {field: 'operationServiceTimeMinutes', label: 'Operation Service Time (min)'},
  {field: 'maxServiceTimeMinutes', label: 'Max Service Time (min)'}
];

// One row sets one validation template of a program. The template may be typed as id or name;
// resolved rows show the template's name.
const serviceValidationsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'validationTemplate', label: 'Validation Template', valuesField: 'templateName'},
  {field: 'state', label: 'State'},
  {field: 'pickupDisplay', label: 'Pickup Display'},
  {field: 'dropoffDisplay', label: 'Dropoff Display'},
  {field: 'fieldServiceDisplay', label: 'Field Service Display'},
  {field: 'enabled', label: 'Enabled'},
  {field: 'defaultValidation', label: 'Default'}
];

// One row sets one subtask template of a program. The template may be typed as id or title;
// resolved rows show the template's title.
const subTasksColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'subTaskTemplate', label: 'Subtask Template', valuesField: 'templateTitle'},
  {field: 'state', label: 'State'},
  {field: 'pickupDisplay', label: 'Pickup Display'},
  {field: 'dropoffDisplay', label: 'Dropoff Display'},
  {field: 'fieldServiceDisplay', label: 'Field Service Display'},
  {field: 'numericOrder', label: 'Numeric Order'},
  {field: 'enabled', label: 'Enabled'},
  {field: 'defaultSubTask', label: 'Default'}
];

// One row sets all scheduling settings of one account + program; an empty cell clears the value.
// Urgency factors are typed in percent and durations in minutes.
const schedulingSettingsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'deliveryDays', label: 'Delivery Days'},
  {field: 'timeSlots', label: 'Time Slots'},
  {field: 'timeSlotSwitch', label: 'Time Slot Switch'},
  {field: 'largeTaskSwitch', label: 'Large Task Switch'},
  {field: 'largeTaskMinimumMinutes', label: 'Large Task Minimum (min)'},
  {field: 'largeTaskTimeWindowFactor', label: 'Large Task Time Window Factor'},
  {field: 'urgencySwitch', label: 'Urgency Switch'},
  {field: 'rushFactor', label: 'Rush Factor (%)'},
  {field: 'rushDuration', label: 'Rush Duration (min)'},
  {field: 'rushEnabled', label: 'Rush Enabled'},
  {field: 'urgentFactor', label: 'Urgent Factor (%)'},
  {field: 'urgentDuration', label: 'Urgent Duration (min)'},
  {field: 'urgentEnabled', label: 'Urgent Enabled'},
  {field: 'normalFactor', label: 'Normal Factor (%)'},
  {field: 'normalDuration', label: 'Normal Duration (min)'},
  {field: 'normalEnabled', label: 'Normal Enabled'},
  {field: 'otherFactor', label: 'Other Factor (%)'},
  {field: 'otherDuration', label: 'Other Duration (min)'},
  {field: 'otherEnabled', label: 'Other Enabled'},
  {field: 'defaultUrgency', label: 'Default Urgency'},
  {field: 'dynamicTimeSlotSwitch', label: 'Dynamic Time Slot Switch'},
  {field: 'dynamicQuoteDurationMinutes', label: 'Dynamic Quote Duration (min)'},
  {field: 'timeWindowGapDays', label: 'Time Window Gap Days'},
  {field: 'freestyleSwitch', label: 'Freestyle Switch'},
  {field: 'perTaskSwitch', label: 'Per Task Switch'},
  {field: 'freestyleMinRange', label: 'Freestyle Time Window Min Range'},
  {field: 'freestyleMaxRange', label: 'Freestyle Time Window Max Range'},
  {field: 'defaultFullDayForServiceDispatch', label: 'Default Full Day For Service Dispatch'},
  {field: 'fullDayPickupTask', label: 'Full Day Pickup Task'},
  {field: 'fullDayDestinationTask', label: 'Full Day Destination Task'},
  {field: 'obligateTimeWindow', label: 'Obligate Time Window'}
];

// One row sets one addition of a program. The addition may be typed as id or name;
// resolved rows show the addition's name.
const additionsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'addition', label: 'Addition', valuesField: 'additionName'},
  {field: 'state', label: 'State'},
  {field: 'price', label: 'Price'},
  {field: 'enabled', label: 'Enabled'}
];

// One row holds at most one quantity line; rows with the same origin, destination and package
// form one unit. Locations and packages are shown as the user typed them (ids or names).
const serviceSettingsColumns: ProgramImportColumn[] = [
  {field: 'accountId', label: 'Account Id'},
  {field: 'programName', label: 'Program Name'},
  {field: 'originType', label: 'Origin Type'},
  {field: 'origin', label: 'Origin'},
  {field: 'destinationType', label: 'Destination Type'},
  {field: 'destination', label: 'Destination'},
  {field: 'packageType', label: 'Package Type'},
  {field: 'parentLink', label: 'Parent Link'},
  {field: 'price', label: 'Price'},
  {field: 'quantityFrom', label: 'Qty From'},
  {field: 'quantityTo', label: 'Qty To'},
  {field: 'quantityType', label: 'Qty Type'},
  {field: 'quantityValue', label: 'Qty Value'}
];

/**
 * Program sections that support file import (PRD-1726). Adding a section is one entry here: the
 * import view, upload dialog and field-mapping panel are all driven by this list.
 *
 * `entity` is the server SchemaEntityType used for mapping schemas. Validate and import live under
 * `importerPath`, while batch status is shared by all sections under `statusPath`
 * (PricePlanImporterController, SRV-15280 Service Time, SRV-15282 Skills, SRV-15276 Hours of Operation,
 * SRV-15305 Vehicle Settings, SRV-15294 Quantity Settings, SRV-15292 Price Settings,
 * SRV-15288 POD Settings, SRV-15290 Package Settings, SRV-15278 Agent Validation,
 * SRV-15284 Sub Tasks, SRV-15275 Scheduling Settings, SRV-15286 Additions,
 * SRV-15295 Service Settings).
 */
export const PROGRAM_IMPORT_TYPES: ProgramImportType[] = [
  {
    id: 'service-time',
    label: 'Service Time',
    entity: 'PricePlanServiceTime',
    importerPath: 'importers-price-plans/service-time',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: serviceTimeColumns
  },
  {
    id: 'skills',
    label: 'Skills',
    entity: 'PricePlanSkills',
    importerPath: 'importers-price-plans/skills',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: skillsColumns,
    groupRowsKey: 'skills'
  },
  {
    id: 'hours-of-operation',
    label: 'Hours Of Operation',
    entity: 'PricePlanHoursOfOperation',
    importerPath: 'importers-price-plans/hours-of-operation',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: hoursOfOperationColumns,
    groupRowsKey: 'hours',
    // The server replaces each imported program's whole week and skips a group with any error.
    weekPreview: true,
    hint: 'The import replaces the whole week: weekdays missing from the file are cleared and fall back to the parent program. A program with any error row is not imported.'
  },
  {
    id: 'vehicle-settings',
    label: 'Vehicle Settings',
    entity: 'PricePlanVehicleSettings',
    importerPath: 'importers-price-plans/vehicle-settings',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: vehicleSettingsColumns,
    groupRowsKey: 'vehicleSettings',
    // Unlike Hours of Operation, vehicles are updated one by one and the rest stay untouched.
    hint: 'Vehicles not in the file keep their settings. Default = true replaces the program\'s default vehicle. An empty Vehicle Surcharge clears the surcharge; an empty Enabled removes the override.'
  },
  {
    id: 'quantity-settings',
    label: 'Quantity Settings',
    entity: 'PricePlanQuantitySettings',
    importerPath: 'importers-price-plans/quantity-settings',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: quantitySettingsColumns,
    groupRowsKey: 'quantityLines',
    removeGroupAsWhole: true,
    // Like Hours of Operation, the server replaces each program's whole list and skips a group with any error.
    hint: 'The import replaces all quantity lines of each program in the file; programs not in the file keep their lines. An empty To means no upper limit and must be the last line. A program with any error row is not imported. Removing a row removes all rows of its program.'
  },
  {
    id: 'price-settings',
    label: 'Price Settings',
    entity: 'PricePlanPriceSettings',
    importerPath: 'importers-price-plans/price-settings',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: priceSettingsColumns,
    // Flat rows like Service Time: the server resolves accountId / programType per row.
    hint: 'One row per account and program. An empty cell or a missing column clears the value, and the program inherits it from the parent program again. The program screen may show the old values for up to 30 minutes.',
    importConfirm: 'Empty cells and missing columns clear the price settings of the imported programs, which then inherit the parent program values. Continue?'
  },
  {
    id: 'pod-settings',
    label: 'POD Settings',
    entity: 'PricePlanPodSettings',
    importerPath: 'importers-price-plans/pod-settings',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: podSettingsColumns,
    groupRowsKey: 'podSettings',
    // Like Vehicle Settings, only the fields in the file are updated and the rest stay untouched.
    hint: 'POD fields not in the file keep their settings. An empty Parent Relation or Mandatory removes that value, and the program inherits it from the parent program again. Visit Summary exists only for Pickup and Dropoff.'
  },
  {
    id: 'package-settings',
    label: 'Package Settings',
    entity: 'PricePlanPackageSettings',
    importerPath: 'importers-price-plans/package-settings',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: packageSettingsColumns,
    groupRowsKey: 'packageSettings',
    // Like Vehicle Settings, only the package sizes in the file are updated and the rest stay untouched.
    hint: 'Package sizes not in the file keep their settings. An empty cell removes that value, and the program inherits it from the parent program again. Default = true replaces the program\'s default package. Service times are in minutes.'
  },
  {
    id: 'service-validations',
    label: 'Agent Validation',
    entity: 'PricePlanServiceValidations',
    importerPath: 'importers-price-plans/service-validations',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: serviceValidationsColumns,
    groupRowsKey: 'validations',
    // Unlike the program screen, which replaces the whole list, only the templates in the file change.
    hint: 'Validation templates not in the file keep their settings. A program can have at most 2 validations, including inherited ones. State = Inherited removes the program\'s own settings. The three Display cells must be filled together.'
  },
  {
    id: 'subtasks',
    label: 'Sub Tasks',
    entity: 'PricePlanSubTasks',
    importerPath: 'importers-price-plans/subtasks',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: subTasksColumns,
    groupRowsKey: 'subTasks',
    // Like Agent Validation, only the templates in the file change, unlike the program screen.
    hint: 'Subtask templates not in the file keep their settings. State = Inherited removes the program\'s own settings. The three Display cells must be filled together. An empty Numeric Order, Enabled or Default falls back to the parent program.'
  },
  {
    id: 'scheduling-settings',
    label: 'Scheduling Settings',
    entity: 'PricePlanSchedulingSettings',
    importerPath: 'importers-price-plans/scheduling-settings',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: schedulingSettingsColumns,
    // Flat rows like Price Settings: every column is written, so an empty cell clears the value.
    hint: 'One row per account and program. Every column is written: an empty cell or a missing column clears the value, and the program inherits it from the parent program again. Time slots and delivery days use the site time zone.',
    importConfirm: 'Empty cells and missing columns clear the scheduling settings of the imported programs, which then inherit the parent program values. Continue?'
  },
  {
    id: 'additions',
    label: 'Additions',
    entity: 'PricePlanAdditions',
    importerPath: 'importers-price-plans/additions',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: additionsColumns,
    groupRowsKey: 'additions',
    // Like Sub Tasks, only the additions in the file change, unlike the program screen.
    hint: 'Additions not in the file keep their settings. Only additions of the parent program can be overridden. State = Inherited removes the program\'s own settings. An empty Price or Enabled falls back to the parent program.'
  },
  {
    id: 'service-settings',
    label: 'Service Settings',
    entity: 'PricePlanServiceSettings',
    importerPath: 'importers-price-plans/service-settings',
    statusPath: 'importers-price-plans',
    permissions: PROGRAM_IMPORT_PERMISSIONS,
    columns: serviceSettingsColumns,
    groupRowsKey: 'units',
    unitRowsKey: 'rows',
    // Only the price and quantity lines of each origin, destination and package in the file change.
    hint: 'Rows with the same origin, destination and package are saved together, and an error in one of them blocks all of them. Only the price and quantity lines change; a new origin and destination pair is created. Parent Link = true uses the parent program values. Removing a row removes all rows of its origin, destination and package.'
  }
];
